import React, { useEffect, useState, useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardSidebar } from '../components/dashboard/DashboardSidebar';
import { SearchIcon, FilterIcon, BookmarkIcon, DownloadIcon, BellIcon, ArrowDownIcon, ArrowUpIcon, CheckIcon, GridIcon, ListIcon, XIcon, ChevronDownIcon, ClockIcon, GlobeIcon, FileTextIcon, BuildingIcon, TagIcon, CalendarIcon, MapPinIcon, AlertCircleIcon, EyeIcon, ShareIcon, StarIcon, ChevronRightIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
// Types
interface Tender {
  id: string;
  title: string;
  reference: string;
  description: string;
  type: string;
  sector: string;
  subSector?: string;
  location: string;
  country: string;
  organization: string;
  publishDate: Date;
  expiryDate: Date;
  budget?: string;
  status: 'open' | 'closing_soon' | 'closed';
  premium: boolean;
  matchScore?: number;
  viewCount: number;
  documentCount: number;
  isSaved: boolean;
  hasAlert: boolean;
}
interface FilterOption {
  id: string;
  label: string;
  count?: number;
  selected?: boolean;
  children?: FilterOption[];
  expanded?: boolean;
}
// Sample data
const tenders: Tender[] = [{
  id: '07182025',
  title: 'Travaux génie civile',
  reference: 'TGC-2025-1453',
  description: 'Travaux de génie civile pour la Direction Régionale de Nabeul Bouargoub',
  type: 'Avis de consultation',
  sector: 'BTP & Construction',
  subSector: 'Génie civil',
  location: 'Nabeul',
  country: 'Tunisie',
  organization: 'Direction Régionale de Nabeul Bouargoub',
  publishDate: new Date('2025-10-24T16:04:00'),
  expiryDate: new Date('2025-11-07'),
  budget: '450 000 TND',
  status: 'open',
  premium: true,
  matchScore: 92,
  viewCount: 47,
  documentCount: 3,
  isSaved: false,
  hasAlert: false
}, {
  id: '08102025',
  title: 'Acquisition de matériels informatiques',
  reference: 'AMI-2025-289',
  description: 'Acquisition de matériels informatiques pour les bureaux régionaux',
  type: 'Avis de consultation',
  sector: 'Technologies & Télécoms',
  subSector: 'Matériels et Équipements Informatique',
  location: 'Tunis',
  country: 'Tunisie',
  organization: 'Hôpital Habib Thameur Sfax',
  publishDate: new Date('2025-10-24T16:04:00'),
  expiryDate: new Date('2025-11-08'),
  budget: '120 000 TND',
  status: 'open',
  premium: true,
  matchScore: 88,
  viewCount: 32,
  documentCount: 2,
  isSaved: true,
  hasAlert: true
}, {
  id: '08102023',
  title: 'Concours SPIE Startup Challenge 2026 – Appel à candidatures',
  reference: 'SPIE-2026-001',
  description: 'Concours SPIE Startup Challenge 2026 – Appel à candidatures organisé par The International Society of Optics and Photonics',
  type: 'Avis de candidature',
  sector: 'Technologies & Télécoms',
  subSector: 'Services Numériques',
  location: 'International',
  country: 'Tunisie',
  organization: 'SPIE — The International Society of Optics and Photonics',
  publishDate: new Date('2025-10-24T13:13:00'),
  expiryDate: new Date('2025-11-08'),
  status: 'open',
  premium: false,
  matchScore: 65,
  viewCount: 18,
  documentCount: 1,
  isSaved: false,
  hasAlert: false
}, {
  id: '25102025',
  title: "Accélérez la croissance de votre projet : rejoignez la 2e cohorte Lab'ess !",
  reference: 'LAB-2025-002',
  description: "Accélérez la croissance de votre projet : rejoignez la 2e cohorte Lab'ess ! Programme d'accompagnement pour startups sociales.",
  type: 'Avis de candidature',
  sector: 'Services & Conseil',
  subSector: 'Conseil en Stratégie',
  location: 'Tunis',
  country: 'Tunisie',
  organization: "LAB'ESS",
  publishDate: new Date('2025-10-24T11:06:00'),
  expiryDate: new Date('2025-12-25'),
  status: 'open',
  premium: true,
  matchScore: 76,
  viewCount: 24,
  documentCount: 2,
  isSaved: false,
  hasAlert: true
}, {
  id: '15102025',
  title: 'Boostez votre impact et développez votre projet à fort impact social !',
  reference: 'SIBC-2025-157',
  description: 'SIBC Afrique du Nord – Appel à candidatures 2025 : Boostez votre impact et développez votre projet à fort impact social !',
  type: 'Avis de candidature',
  sector: 'Services & Conseil',
  subSector: 'Marketing Digital',
  location: 'International',
  country: 'Tunisie',
  organization: 'SIBC Afrique du Nord',
  publishDate: new Date('2025-10-24T11:06:00'),
  expiryDate: new Date('2025-11-15'),
  status: 'closing_soon',
  premium: true,
  matchScore: 82,
  viewCount: 41,
  documentCount: 3,
  isSaved: true,
  hasAlert: false
}, {
  id: '15102023',
  title: "Fourniture d'articles électricité",
  reference: 'FAE-2025-489',
  description: "Fourniture d'articles électriques industriels pour les installations pharmaceutiques",
  type: 'Avis de consultation',
  sector: 'Énergie & Utilities',
  subSector: 'Équipements Électriques',
  location: 'Tunis',
  country: 'Tunisie',
  organization: 'Société des Industries Pharmaceutiques de Tunisie - SIPHAT',
  publishDate: new Date('2025-10-24T11:06:00'),
  expiryDate: new Date('2025-11-15'),
  budget: '75 000 TND',
  status: 'closing_soon',
  premium: false,
  matchScore: 71,
  viewCount: 29,
  documentCount: 2,
  isSaved: false,
  hasAlert: false
}];
// Filter options
const sectorOptions: FilterOption[] = [{
  id: 'all',
  label: 'Tous les secteurs',
  count: 134
}, {
  id: 'tech',
  label: 'Technologies & Télécoms',
  count: 38,
  expanded: false,
  children: [{
    id: 'tech-materiel',
    label: 'Matériels et Équipements Informatique',
    count: 15
  }, {
    id: 'tech-logiciels',
    label: 'Logiciels et Solutions',
    count: 12
  }, {
    id: 'tech-services',
    label: 'Services Numériques',
    count: 8
  }, {
    id: 'tech-telecom',
    label: 'Infrastructures Télécoms',
    count: 7
  }]
}, {
  id: 'marketing',
  label: 'Marketing Digital',
  count: 32,
  expanded: false,
  children: [{
    id: 'marketing-influence',
    label: "Campagnes de Marketing d'Influence",
    count: 8
  }, {
    id: 'marketing-email',
    label: 'Automatisation du Marketing par Email',
    count: 7
  }, {
    id: 'marketing-contenu',
    label: 'Stratégies de Marketing de Contenu',
    count: 9
  }, {
    id: 'marketing-social',
    label: 'Marketing et Gestion des Médias Sociaux',
    count: 10
  }, {
    id: 'marketing-ppc',
    label: 'Gestion de la Publicité au Paiement Par Clic (PPC)',
    count: 6
  }, {
    id: 'marketing-seo',
    label: "Services d'Optimisation pour les Moteurs de Recherche (SEO)",
    count: 5
  }]
}, {
  id: 'construction',
  label: 'BTP & Construction',
  count: 42,
  expanded: false,
  children: [{
    id: 'construction-batiment',
    label: 'Bâtiment',
    count: 14
  }, {
    id: 'construction-genie',
    label: 'Génie civil',
    count: 12
  }, {
    id: 'construction-infra',
    label: 'Infrastructures routières',
    count: 9
  }, {
    id: 'construction-materiaux',
    label: 'Matériaux de construction',
    count: 7
  }]
}, {
  id: 'energy',
  label: 'Énergie & Utilities',
  count: 27,
  expanded: false,
  children: [{
    id: 'energy-renouvelable',
    label: 'Énergies renouvelables',
    count: 10
  }, {
    id: 'energy-electricite',
    label: 'Équipements Électriques',
    count: 8
  }, {
    id: 'energy-eau',
    label: "Traitement de l'eau",
    count: 5
  }, {
    id: 'energy-gaz',
    label: 'Gaz et pétrole',
    count: 4
  }]
}, {
  id: 'services',
  label: 'Services & Conseil',
  count: 23,
  expanded: false,
  children: [{
    id: 'services-strategie',
    label: 'Conseil en Stratégie',
    count: 7
  }, {
    id: 'services-finance',
    label: 'Services Financiers',
    count: 6
  }, {
    id: 'services-juridique',
    label: 'Conseil Juridique',
    count: 5
  }, {
    id: 'services-rh',
    label: 'Ressources Humaines',
    count: 5
  }]
}, {
  id: 'health',
  label: 'Santé & Pharmaceutique',
  count: 19,
  expanded: false,
  children: [{
    id: 'health-equipement',
    label: 'Équipements médicaux',
    count: 8
  }, {
    id: 'health-pharma',
    label: 'Produits pharmaceutiques',
    count: 7
  }, {
    id: 'health-services',
    label: 'Services de santé',
    count: 4
  }]
}, {
  id: 'vehicles',
  label: 'Véhicules et Accessoires',
  count: 17,
  expanded: false,
  children: [{
    id: 'vehicles-auto',
    label: 'Automobiles',
    count: 6
  }, {
    id: 'vehicles-pieces',
    label: 'Pièces détachées',
    count: 5
  }, {
    id: 'vehicles-transport',
    label: 'Transport collectif',
    count: 4
  }, {
    id: 'vehicles-special',
    label: 'Véhicules spéciaux',
    count: 2
  }]
}, {
  id: 'agriculture',
  label: 'Agriculture & Agroalimentaire',
  count: 15,
  expanded: false,
  children: [{
    id: 'agriculture-equipement',
    label: 'Équipements agricoles',
    count: 5
  }, {
    id: 'agriculture-production',
    label: 'Production alimentaire',
    count: 4
  }, {
    id: 'agriculture-irrigation',
    label: "Systèmes d'irrigation",
    count: 3
  }, {
    id: 'agriculture-stockage',
    label: 'Stockage et conservation',
    count: 3
  }]
}, {
  id: 'transport',
  label: 'Transport & Logistique',
  count: 12,
  expanded: false,
  children: [{
    id: 'transport-fret',
    label: 'Fret et livraison',
    count: 4
  }, {
    id: 'transport-stockage',
    label: 'Entreposage',
    count: 3
  }, {
    id: 'transport-gestion',
    label: 'Gestion de flotte',
    count: 3
  }, {
    id: 'transport-logistique',
    label: 'Solutions logistiques',
    count: 2
  }]
}, {
  id: 'education',
  label: 'Éducation & Formation',
  count: 9,
  expanded: false,
  children: [{
    id: 'education-materiel',
    label: 'Matériel pédagogique',
    count: 3
  }, {
    id: 'education-formation',
    label: 'Services de formation',
    count: 3
  }, {
    id: 'education-elearning',
    label: 'Solutions e-learning',
    count: 3
  }]
}];
const countryOptions: FilterOption[] = [{
  id: 'all',
  label: 'Tous les pays',
  count: 134
}, {
  id: 'tunisia',
  label: 'Tunisie',
  count: 47,
  selected: true
}, {
  id: 'morocco',
  label: 'Maroc',
  count: 32
}, {
  id: 'senegal',
  label: 'Sénégal',
  count: 28
}, {
  id: 'ivory-coast',
  label: "Côte d'Ivoire",
  count: 26
}, {
  id: 'algeria',
  label: 'Algérie',
  count: 23
}, {
  id: 'cameroon',
  label: 'Cameroun',
  count: 18
}, {
  id: 'egypt',
  label: 'Égypte',
  count: 15
}, {
  id: 'kenya',
  label: 'Kenya',
  count: 12
}, {
  id: 'nigeria',
  label: 'Nigeria',
  count: 11
}, {
  id: 'other',
  label: 'Autres pays',
  count: 24
}];
const typeOptions: FilterOption[] = [{
  id: 'all',
  label: 'Tous les types',
  count: 134
}, {
  id: 'consultation',
  label: 'Avis de consultation',
  count: 67
}, {
  id: 'candidature',
  label: 'Avis de candidature',
  count: 42
}, {
  id: 'appel',
  label: "Appel d'offres",
  count: 38
}, {
  id: 'prequalification',
  label: 'Préqualification',
  count: 15
}, {
  id: 'expression',
  label: "Expression d'intérêt",
  count: 12
}, {
  id: 'other',
  label: 'Autres',
  count: 8
}];
const statusOptions: FilterOption[] = [{
  id: 'all',
  label: 'Tous les statuts',
  count: 134
}, {
  id: 'open',
  label: 'En cours',
  count: 98
}, {
  id: 'closing_soon',
  label: 'Clôture proche',
  count: 22
}, {
  id: 'closed',
  label: 'Clôturés',
  count: 14
}];
// Main component
export function PremiumTendersPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [sortBy, setSortBy] = useState<'relevance' | 'date' | 'expiry'>('relevance');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [sectorSearchQuery, setSectorSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({
    country: ['tunisia'],
    sector: [],
    type: [],
    status: []
  });
  const [sectorOptionsState, setSectorOptionsState] = useState<FilterOption[]>(sectorOptions);
  // Effect to filter sectors based on search query
  useEffect(() => {
    if (!sectorSearchQuery.trim()) {
      setSectorOptionsState(sectorOptions);
      return;
    }
    const query = sectorSearchQuery.toLowerCase().trim();
    // Filter sectors and their subsectors based on the search query
    const filteredSectors = sectorOptions.filter(sector => {
      if (sector.id === 'all') return true;
      // Check if main sector matches
      if (sector.label.toLowerCase().includes(query)) return true;
      // Check if any subsector matches and update children accordingly
      if (sector.children) {
        const matchingChildren = sector.children.filter(child => child.label.toLowerCase().includes(query));
        if (matchingChildren.length > 0) {
          return true;
        }
      }
      return false;
    }).map(sector => {
      if (sector.id === 'all' || !sector.children) return sector;
      // If sector matches directly, include all children
      if (sector.label.toLowerCase().includes(query)) return {
        ...sector,
        expanded: true
      };
      // Otherwise, only include matching children
      return {
        ...sector,
        expanded: true,
        children: sector.children.filter(child => child.label.toLowerCase().includes(query))
      };
    });
    setSectorOptionsState(filteredSectors);
  }, [sectorSearchQuery]);
  // Mock user data
  const user = {
    name: 'FATHALLAH Mehdi',
    company: 'Tunipages',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    email: 'mehdi.fathallah@tunipages.com',
    phone: '+33 7 53 29 34 19',
    plan: 'Premium Afrique'
  };
  // Toggle sector expansion
  const toggleSectorExpansion = (sectorId: string) => {
    setSectorOptionsState(prevState => prevState.map(sector => {
      if (sector.id === sectorId) {
        return {
          ...sector,
          expanded: !sector.expanded
        };
      }
      return sector;
    }));
  };
  // Filter handlers
  const toggleFilter = (category: string, id: string, isSubsector = false, parentId?: string) => {
    setSelectedFilters(prev => {
      const updated = {
        ...prev
      };
      // Handle main sector selection
      if (!isSubsector) {
        // If selecting "all", clear all other selections in this category
        if (id === 'all') {
          updated[category] = [];
          return updated;
        }
        const sector = sectorOptions.find(s => s.id === id);
        // If this is a sector with children and it's being selected
        if (sector?.children && !prev[category].includes(id)) {
          // Add the sector and all its children
          const childrenIds = sector.children.map(child => child.id);
          updated[category] = [...prev[category].filter(i => i !== 'all'), id, ...childrenIds];
          return updated;
        }
        // If this is a sector with children and it's being unselected
        if (sector?.children && prev[category].includes(id)) {
          // Remove the sector and all its children
          const childrenIds = sector.children.map(child => child.id);
          updated[category] = prev[category].filter(i => i !== id && !childrenIds.includes(i));
          return updated;
        }
      }
      // Handle subsector selection
      if (isSubsector && parentId) {
        const parentSector = sectorOptions.find(s => s.id === parentId);
        if (parentSector?.children) {
          // If selecting a subsector
          if (!prev[category].includes(id)) {
            updated[category] = [...prev[category].filter(i => i !== 'all'), id];
            // Check if all subsectors of this parent are now selected
            const allSubsectorIds = parentSector.children.map(child => child.id);
            const selectedSubsectors = allSubsectorIds.filter(sid => updated[category].includes(sid));
            // If all subsectors are selected, also select the parent
            if (selectedSubsectors.length === allSubsectorIds.length) {
              updated[category].push(parentId);
            }
          } else {
            // If unselecting a subsector, also unselect the parent
            updated[category] = prev[category].filter(i => i !== id && i !== parentId);
          }
        }
      }
      // Default toggle behavior for simple cases
      if (!isSubsector && !sectorOptions.find(s => s.id === id)?.children) {
        if (updated[category].includes(id)) {
          updated[category] = updated[category].filter(i => i !== id);
        } else {
          updated[category] = [...updated[category].filter(i => i !== 'all'), id];
        }
      }
      return updated;
    });
  };
  const clearFilter = (category: string) => {
    setSelectedFilters(prev => ({
      ...prev,
      [category]: []
    }));
  };
  const clearAllFilters = () => {
    setSelectedFilters({
      country: [],
      sector: [],
      type: [],
      status: []
    });
    setSearchQuery('');
    setSectorSearchQuery('');
  };
  // Toggle sort order
  const handleSortChange = (sort: 'relevance' | 'date' | 'expiry') => {
    if (sortBy === sort) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(sort);
      setSortOrder('desc');
    }
  };
  // Toggle save/bookmark
  const toggleSave = (id: string) => {
    // In a real application, this would call an API to save the tender
    console.log(`Toggle save for tender ${id}`);
  };
  // Toggle alert
  const toggleAlert = (id: string) => {
    // In a real application, this would call an API to set an alert
    console.log(`Toggle alert for tender ${id}`);
  };
  // Check if a sector or subsector is selected
  const isSectorSelected = (id: string, isSubsector = false, parentId?: string) => {
    return selectedFilters.sector.includes(id);
  };
  // Filter and sort tenders
  const filteredTenders = tenders.filter(tender => {
    // Search query filter
    if (searchQuery && !tender.title.toLowerCase().includes(searchQuery.toLowerCase()) && !tender.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    // Country filter
    if (selectedFilters.country.length > 0 && !selectedFilters.country.some(c => tender.country.toLowerCase().includes(c.toLowerCase()))) {
      return false;
    }
    // Sector filter
    if (selectedFilters.sector.length > 0 && !(selectedFilters.sector.some(s => tender.sector.toLowerCase().includes(s.toLowerCase())) || tender.subSector && selectedFilters.sector.some(s => tender.subSector?.toLowerCase().includes(s.toLowerCase())))) {
      return false;
    }
    // Type filter
    if (selectedFilters.type.length > 0 && !selectedFilters.type.some(t => tender.type.toLowerCase().includes(t.toLowerCase()))) {
      return false;
    }
    // Status filter
    if (selectedFilters.status.length > 0 && !selectedFilters.status.includes(tender.status)) {
      return false;
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === 'relevance') {
      return sortOrder === 'desc' ? (b.matchScore || 0) - (a.matchScore || 0) : (a.matchScore || 0) - (b.matchScore || 0);
    } else if (sortBy === 'date') {
      return sortOrder === 'desc' ? b.publishDate.getTime() - a.publishDate.getTime() : a.publishDate.getTime() - b.publishDate.getTime();
    } else {
      return sortOrder === 'desc' ? b.expiryDate.getTime() - a.expiryDate.getTime() : a.expiryDate.getTime() - b.expiryDate.getTime();
    }
  });
  return <div className="min-h-screen bg-gray-50" data-id="element-3036">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-gray-800 bg-opacity-50 z-20 md:hidden" onClick={() => setSidebarOpen(false)} data-id="element-3037" />}
      {/* Sidebar */}
      <DashboardSidebar open={sidebarOpen} setOpen={setSidebarOpen} data-id="element-3038" />
      {/* Main content */}
      <div className="md:pl-64" data-id="element-3039">
        {/* Header */}
        <DashboardHeader user={user} onMenuClick={() => setSidebarOpen(true)} data-id="element-3040" />
        {/* Main dashboard content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8" data-id="element-3041">
          {/* Page header */}
          <div className="mb-6" data-id="element-3042">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-3043">
              <div data-id="element-3044">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-3045">
                  Liste des appels d'
                  <span className="text-red-600" data-id="element-3046">offres</span>
                </h1>
                <p className="mt-1 text-sm text-gray-600" data-id="element-3047">
                  DÉCOUVREZ TOUS LES APPELS D'OFFRES QUI VOUS INTÉRESSENT
                </p>
              </div>
              {/* Premium badge */}
              <div className="mt-4 md:mt-0 flex items-center bg-gradient-to-r from-yellow-500 to-amber-500 text-white px-4 py-2 rounded-full shadow-md" data-id="element-3048">
                <StarIcon size={18} className="mr-2" data-id="element-3049" />
                <span className="font-semibold" data-id="element-3050">Compte Premium Afrique</span>
              </div>
            </div>
            {/* Statistics cards */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-3051">
              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100" data-id="element-3052">
                <div className="text-sm text-gray-500 mb-1" data-id="element-3053">
                  Appels d'offres actifs
                </div>
                <div className="text-2xl font-bold text-blue-900" data-id="element-3054">1,254</div>
                <div className="text-xs text-green-600 flex items-center mt-1" data-id="element-3055">
                  <ArrowUpIcon size={12} className="mr-1" data-id="element-3056" />
                  +15% depuis la semaine dernière
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100" data-id="element-3057">
                <div className="text-sm text-gray-500 mb-1" data-id="element-3058">
                  Nouveaux aujourd'hui
                </div>
                <div className="text-2xl font-bold text-blue-900" data-id="element-3059">127</div>
                <div className="text-xs text-green-600 flex items-center mt-1" data-id="element-3060">
                  <ArrowUpIcon size={12} className="mr-1" data-id="element-3061" />
                  +23% par rapport à hier
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100" data-id="element-3062">
                <div className="text-sm text-gray-500 mb-1" data-id="element-3063">
                  Clôtures prochaines
                </div>
                <div className="text-2xl font-bold text-orange-600" data-id="element-3064">89</div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-3065">
                  Dans les 7 prochains jours
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100" data-id="element-3066">
                <div className="text-sm text-gray-500 mb-1" data-id="element-3067">
                  Correspondance élevée
                </div>
                <div className="text-2xl font-bold text-green-600" data-id="element-3068">42</div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-3069">
                  Score de correspondance &gt; 80%
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col lg:flex-row gap-6" data-id="element-3070">
            {/* Filters sidebar */}
            <div className="w-full lg:w-64 flex-shrink-0" data-id="element-3071">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden sticky top-20" data-id="element-3072">
                <div className="p-4 border-b border-gray-100 flex items-center justify-between" data-id="element-3073">
                  <div className="font-medium text-gray-800 flex items-center" data-id="element-3074">
                    <FilterIcon size={18} className="mr-2 text-blue-600" data-id="element-3075" />
                    Filtres
                  </div>
                  {/* Clear all filters button */}
                  {Object.values(selectedFilters).some(filters => filters.length > 0) && <button onClick={clearAllFilters} className="text-sm text-blue-600 hover:text-blue-800" data-id="element-3076">
                      Réinitialiser
                    </button>}
                </div>
                <div className="p-4 border-b border-gray-100" data-id="element-3077">
                  <div className="flex items-center justify-between mb-2" data-id="element-3078">
                    <h3 className="font-medium text-gray-700" data-id="element-3079">Pays</h3>
                    {selectedFilters.country.length > 0 && <button onClick={() => clearFilter('country')} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-3080">
                        Effacer
                      </button>}
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-2" data-id="element-3081">
                    {countryOptions.map(option => <div key={option.id} className="flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 py-1 rounded" onClick={() => toggleFilter('country', option.id)} data-id="element-3082">
                        <div className="flex items-center" data-id="element-3083">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center mr-2 ${selectedFilters.country.includes(option.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`} data-id="element-3084">
                            {selectedFilters.country.includes(option.id) && <CheckIcon size={12} className="text-white" data-id="element-3085" />}
                          </div>
                          <span className="text-sm text-gray-700" data-id="element-3086">
                            {option.label}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500" data-id="element-3087">
                          {option.count}
                        </span>
                      </div>)}
                  </div>
                </div>
                <div className="p-4 border-b border-gray-100" data-id="element-3088">
                  <div className="flex items-center justify-between mb-2" data-id="element-3089">
                    <h3 className="font-medium text-gray-700 flex items-center" data-id="element-3090">
                      <TagIcon size={16} className="mr-1.5 text-red-500" data-id="element-3091" />
                      Secteurs d'activités
                    </h3>
                    {selectedFilters.sector.length > 0 && <button onClick={() => clearFilter('sector')} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-3092">
                        Effacer
                      </button>}
                  </div>
                  {/* Search input for sectors */}
                  <div className="relative mb-3" data-id="element-3093">
                    <input type="text" className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Rechercher une activité..." value={sectorSearchQuery} onChange={e => setSectorSearchQuery(e.target.value)} data-id="element-3094" />
                    <SearchIcon size={14} className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-3095" />
                    {sectorSearchQuery && <button className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setSectorSearchQuery('')} data-id="element-3096">
                        <XIcon size={12} data-id="element-3097" />
                      </button>}
                  </div>
                  <div className="space-y-1 max-h-60 overflow-y-auto pr-2" data-id="element-3098">
                    {/* "All sectors" option */}
                    <div className="flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 py-1 rounded" onClick={() => toggleFilter('sector', 'all')} data-id="element-3099">
                      <div className="flex items-center" data-id="element-3100">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center mr-2 ${selectedFilters.sector.length === 0 ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`} data-id="element-3101">
                          {selectedFilters.sector.length === 0 && <CheckIcon size={12} className="text-white" data-id="element-3102" />}
                        </div>
                        <span className="text-sm text-gray-700" data-id="element-3103">
                          Tous les secteurs
                        </span>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-3104">
                        {sectorOptionsState.find(s => s.id === 'all')?.count || 0}
                      </span>
                    </div>
                    {/* Sector options with children */}
                    {sectorOptionsState.filter(sector => sector.id !== 'all').map(sector => <div key={sector.id} data-id="element-3105">
                          <div className="flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 py-1.5 rounded" data-id="element-3106">
                            <div className="flex items-center flex-grow" data-id="element-3107">
                              <div className={`w-4 h-4 rounded border flex items-center justify-center mr-2 ${isSectorSelected(sector.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`} onClick={e => {
                          e.stopPropagation();
                          toggleFilter('sector', sector.id);
                        }} data-id="element-3108">
                                {isSectorSelected(sector.id) && <CheckIcon size={12} className="text-white" data-id="element-3109" />}
                              </div>
                              <span className="text-sm text-gray-700 flex-grow" onClick={e => {
                          e.stopPropagation();
                          toggleFilter('sector', sector.id);
                        }} data-id="element-3110">
                                {sector.label}
                              </span>
                              {/* Expand/collapse button */}
                              {sector.children && sector.children.length > 0 && <button className="ml-auto p-1 text-gray-500 hover:text-gray-700" onClick={() => toggleSectorExpansion(sector.id)} data-id="element-3111">
                                    <ChevronDownIcon size={16} className={`transform transition-transform ${sector.expanded ? 'rotate-180' : ''}`} data-id="element-3112" />
                                  </button>}
                            </div>
                            <span className="text-xs text-gray-500 ml-1" data-id="element-3113">
                              {sector.count}
                            </span>
                          </div>
                          {/* Subsector options */}
                          {sector.expanded && sector.children && <div className="ml-6 space-y-1 mt-1 mb-2" data-id="element-3114">
                              {sector.children.map(subsector => <div key={subsector.id} className="flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 py-1 rounded" onClick={() => toggleFilter('sector', subsector.id, true, sector.id)} data-id="element-3115">
                                  <div className="flex items-center" data-id="element-3116">
                                    <div className={`w-4 h-4 rounded border flex items-center justify-center mr-2 ${isSectorSelected(subsector.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`} data-id="element-3117">
                                      {isSectorSelected(subsector.id) && <CheckIcon size={12} className="text-white" data-id="element-3118" />}
                                    </div>
                                    <span className="text-sm text-gray-700" data-id="element-3119">
                                      {subsector.label}
                                    </span>
                                  </div>
                                  <span className="text-xs text-gray-500" data-id="element-3120">
                                    {subsector.count}
                                  </span>
                                </div>)}
                            </div>}
                        </div>)}
                  </div>
                </div>
                <div className="p-4 border-b border-gray-100" data-id="element-3121">
                  <div className="flex items-center justify-between mb-2" data-id="element-3122">
                    <h3 className="font-medium text-gray-700" data-id="element-3123">Type d'avis</h3>
                    {selectedFilters.type.length > 0 && <button onClick={() => clearFilter('type')} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-3124">
                        Effacer
                      </button>}
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto pr-2" data-id="element-3125">
                    {typeOptions.map(option => <div key={option.id} className="flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 py-1 rounded" onClick={() => toggleFilter('type', option.id)} data-id="element-3126">
                        <div className="flex items-center" data-id="element-3127">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center mr-2 ${selectedFilters.type.includes(option.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`} data-id="element-3128">
                            {selectedFilters.type.includes(option.id) && <CheckIcon size={12} className="text-white" data-id="element-3129" />}
                          </div>
                          <span className="text-sm text-gray-700" data-id="element-3130">
                            {option.label}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500" data-id="element-3131">
                          {option.count}
                        </span>
                      </div>)}
                  </div>
                </div>
                <div className="p-4" data-id="element-3132">
                  <div className="flex items-center justify-between mb-2" data-id="element-3133">
                    <h3 className="font-medium text-gray-700" data-id="element-3134">Statut</h3>
                    {selectedFilters.status.length > 0 && <button onClick={() => clearFilter('status')} className="text-xs text-gray-500 hover:text-gray-700" data-id="element-3135">
                        Effacer
                      </button>}
                  </div>
                  <div className="space-y-2" data-id="element-3136">
                    {statusOptions.map(option => <div key={option.id} className="flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 py-1 rounded" onClick={() => toggleFilter('status', option.id)} data-id="element-3137">
                        <div className="flex items-center" data-id="element-3138">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center mr-2 ${selectedFilters.status.includes(option.id) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`} data-id="element-3139">
                            {selectedFilters.status.includes(option.id) && <CheckIcon size={12} className="text-white" data-id="element-3140" />}
                          </div>
                          <span className="text-sm text-gray-700" data-id="element-3141">
                            {option.label}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500" data-id="element-3142">
                          {option.count}
                        </span>
                      </div>)}
                  </div>
                </div>
                <div className="p-4 bg-blue-50" data-id="element-3143">
                  <Button variant="primary" fullWidth className="shadow-md" data-id="element-3144">
                    Appliquer les filtres
                  </Button>
                  {/* Premium feature: save search */}
                  <div className="mt-4" data-id="element-3145">
                    <button className="w-full flex items-center justify-center text-sm text-blue-700 hover:text-blue-800 font-medium" data-id="element-3146">
                      <BookmarkIcon size={15} className="mr-1.5" data-id="element-3147" />
                      Sauvegarder cette recherche
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* Main content area */}
            <div className="flex-grow" data-id="element-3148">
              {/* Search and controls */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6" data-id="element-3149">
                <div className="p-4" data-id="element-3150">
                  <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-3151">
                    {/* Search input */}
                    <div className="flex-grow relative" data-id="element-3152">
                      <input type="text" placeholder="Recherchez des marchés, ex: 'Fournitures', 'Consulting'..." className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3153" />
                      <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} data-id="element-3154" />
                      {searchQuery && <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setSearchQuery('')} data-id="element-3155">
                          <XIcon size={16} data-id="element-3156" />
                        </button>}
                    </div>
                    {/* Search button */}
                    <Button variant="primary" className="md:w-auto shadow-md" data-id="element-3157">
                      <SearchIcon size={18} className="mr-2" data-id="element-3158" />
                      Rechercher
                    </Button>
                  </div>
                  {/* Active filters */}
                  {Object.entries(selectedFilters).some(([_, filters]) => filters.length > 0) && <div className="mt-4 flex flex-wrap gap-2" data-id="element-3159">
                      {Object.entries(selectedFilters).map(([category, filters]) => filters.map(filter => {
                    let label = filter;
                    // Find the proper label from options
                    if (category === 'country') {
                      label = countryOptions.find(o => o.id === filter)?.label || filter;
                    } else if (category === 'sector') {
                      // Look for label in both main sectors and subsectors
                      let found = false;
                      for (const sector of sectorOptions) {
                        if (sector.id === filter) {
                          label = sector.label;
                          found = true;
                          break;
                        }
                        if (sector.children) {
                          const subsector = sector.children.find(s => s.id === filter);
                          if (subsector) {
                            label = subsector.label;
                            found = true;
                            break;
                          }
                        }
                      }
                      if (!found) {
                        label = filter;
                      }
                    } else if (category === 'type') {
                      label = typeOptions.find(o => o.id === filter)?.label || filter;
                    } else if (category === 'status') {
                      label = statusOptions.find(o => o.id === filter)?.label || filter;
                    }
                    return <div key={`${category}-${filter}`} className="flex items-center bg-blue-50 text-blue-700 text-sm px-3 py-1 rounded-full" data-id="element-3160">
                                <span data-id="element-3161">{label}</span>
                                <button className="ml-1.5 text-blue-500 hover:text-blue-700" onClick={() => toggleFilter(category, filter)} data-id="element-3162">
                                  <XIcon size={14} data-id="element-3163" />
                                </button>
                              </div>;
                  }))}
                    </div>}
                </div>
              </div>
              {/* Results controls */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4" data-id="element-3164">
                <div className="text-sm text-gray-600 mb-3 md:mb-0" data-id="element-3165">
                  <strong data-id="element-3166">{filteredTenders.length}</strong> résultats trouvés
                </div>
                <div className="flex items-center space-x-4" data-id="element-3167">
                  {/* Sort controls */}
                  <div className="flex items-center space-x-2" data-id="element-3168">
                    <span className="text-sm text-gray-600" data-id="element-3169">Trier par:</span>
                    <div className="relative" data-id="element-3170">
                      <select className="appearance-none bg-white border border-gray-300 text-sm rounded-md py-1.5 pl-3 pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" value={sortBy} onChange={e => handleSortChange(e.target.value as 'relevance' | 'date' | 'expiry')} data-id="element-3171">
                        <option value="relevance" data-id="element-3172">Pertinence</option>
                        <option value="date" data-id="element-3173">Date de publication</option>
                        <option value="expiry" data-id="element-3174">Date d'expiration</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-3175">
                        <ChevronDownIcon size={16} className="text-gray-500" data-id="element-3176" />
                      </div>
                    </div>
                    <button className="p-1.5 bg-white border border-gray-300 rounded-md" onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')} data-id="element-3177">
                      {sortOrder === 'asc' ? <ArrowUpIcon size={16} className="text-gray-500" data-id="element-3178" /> : <ArrowDownIcon size={16} className="text-gray-500" data-id="element-3179" />}
                    </button>
                  </div>
                  {/* View controls */}
                  <div className="flex items-center border border-gray-300 rounded-md overflow-hidden" data-id="element-3180">
                    <button className={`p-1.5 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-500'}`} onClick={() => setViewMode('list')} data-id="element-3181">
                      <ListIcon size={16} data-id="element-3182" />
                    </button>
                    <button className={`p-1.5 ${viewMode === 'grid' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-500'}`} onClick={() => setViewMode('grid')} data-id="element-3183">
                      <GridIcon size={16} data-id="element-3184" />
                    </button>
                  </div>
                </div>
              </div>
              {/* Tenders list */}
              {filteredTenders.length > 0 ? <div className={`space-y-4 ${viewMode === 'grid' ? 'md:grid md:grid-cols-2 md:gap-4 md:space-y-0' : ''}`} data-id="element-3185">
                  {filteredTenders.map(tender => <motion.div key={tender.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3
              }} className={`bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden ${tender.premium ? 'ring-2 ring-yellow-300' : ''}`} data-id="element-3186">
                      <div className="flex justify-between items-start p-4 border-b border-gray-100" data-id="element-3187">
                        <div data-id="element-3188">
                          <div className="flex items-center space-x-2 mb-1" data-id="element-3189">
                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700" data-id="element-3190">
                              {tender.type}
                            </span>
                            {tender.status === 'closing_soon' && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 flex items-center" data-id="element-3191">
                                <ClockIcon size={12} className="mr-1" data-id="element-3192" />
                                Clôture proche
                              </span>}
                            {tender.premium && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 flex items-center" data-id="element-3193">
                                <StarIcon size={12} className="mr-1" data-id="element-3194" />
                                Premium
                              </span>}
                          </div>
                          <h3 className="font-semibold text-lg text-gray-900 mb-1" data-id="element-3195">
                            {tender.title}
                          </h3>
                          <div className="flex flex-wrap items-center text-sm text-gray-500 gap-x-4 gap-y-1" data-id="element-3196">
                            <div className="flex items-center" data-id="element-3197">
                              <BuildingIcon size={14} className="mr-1 text-gray-400" data-id="element-3198" />
                              {tender.organization}
                            </div>
                            <div className="flex items-center" data-id="element-3199">
                              <MapPinIcon size={14} className="mr-1 text-gray-400" data-id="element-3200" />
                              {tender.location}, {tender.country}
                            </div>
                          </div>
                        </div>
                        {/* Match score for premium users */}
                        {tender.premium && tender.matchScore && <div className="flex-shrink-0 ml-4" data-id="element-3201">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${tender.matchScore >= 80 ? 'bg-green-500' : tender.matchScore >= 60 ? 'bg-yellow-500' : 'bg-orange-500'}`} data-id="element-3202">
                              {tender.matchScore}%
                            </div>
                          </div>}
                      </div>
                      <div className="p-4" data-id="element-3203">
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2" data-id="element-3204">
                          {tender.description}
                        </p>
                        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-3205">
                          <div className="bg-gray-50 p-2 rounded" data-id="element-3206">
                            <div className="text-xs text-gray-500 mb-1" data-id="element-3207">
                              Référence
                            </div>
                            <div className="text-sm font-medium text-gray-800" data-id="element-3208">
                              {tender.reference}
                            </div>
                          </div>
                          {tender.budget && <div className="bg-gray-50 p-2 rounded" data-id="element-3209">
                              <div className="text-xs text-gray-500 mb-1" data-id="element-3210">
                                Budget estimé
                              </div>
                              <div className="text-sm font-medium text-gray-800" data-id="element-3211">
                                {tender.budget}
                              </div>
                            </div>}
                          <div className="bg-gray-50 p-2 rounded" data-id="element-3212">
                            <div className="text-xs text-gray-500 mb-1 flex items-center" data-id="element-3213">
                              <CalendarIcon size={12} className="mr-1" data-id="element-3214" />
                              Publication
                            </div>
                            <div className="text-sm font-medium text-gray-800" data-id="element-3215">
                              {tender.publishDate.toLocaleDateString('fr-FR')}
                            </div>
                          </div>
                          <div className="bg-gray-50 p-2 rounded" data-id="element-3216">
                            <div className="text-xs text-gray-500 mb-1 flex items-center" data-id="element-3217">
                              <ClockIcon size={12} className="mr-1" data-id="element-3218" />
                              Expiration
                            </div>
                            <div className={`text-sm font-medium ${tender.status === 'closing_soon' ? 'text-orange-600' : 'text-gray-800'}`} data-id="element-3219">
                              {tender.expiryDate.toLocaleDateString('fr-FR')}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-3220">
                          <div className="flex items-center space-x-3 text-xs text-gray-500" data-id="element-3221">
                            <div className="flex items-center" data-id="element-3222">
                              <EyeIcon size={14} className="mr-1" data-id="element-3223" />
                              {tender.viewCount} vues
                            </div>
                            <div className="flex items-center" data-id="element-3224">
                              <FileTextIcon size={14} className="mr-1" data-id="element-3225" />
                              {tender.documentCount} document
                              {tender.documentCount !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <div className="flex space-x-2" data-id="element-3226">
                            <button className={`p-2 rounded-full ${tender.isSaved ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`} onClick={() => toggleSave(tender.id)} title="Sauvegarder" data-id="element-3227">
                              <BookmarkIcon size={16} data-id="element-3228" />
                            </button>
                            <button className={`p-2 rounded-full ${tender.hasAlert ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`} onClick={() => toggleAlert(tender.id)} title="Créer une alerte" data-id="element-3229">
                              <BellIcon size={16} data-id="element-3230" />
                            </button>
                            <button className="p-2 rounded-full bg-gray-50 text-gray-500 hover:bg-gray-100" title="Partager" data-id="element-3231">
                              <ShareIcon size={16} data-id="element-3232" />
                            </button>
                            <button className="p-2 rounded-full bg-gray-50 text-gray-500 hover:bg-gray-100" title="Télécharger" data-id="element-3233">
                              <DownloadIcon size={16} data-id="element-3234" />
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 flex justify-between items-center" data-id="element-3235">
                        <div className="text-sm" data-id="element-3236">
                          <span className="text-gray-500" data-id="element-3237">Secteur: </span>
                          <span className="font-medium text-gray-800" data-id="element-3238">
                            {tender.sector}
                            {tender.subSector && <span className="text-gray-600" data-id="element-3239">
                                {' > '}
                                {tender.subSector}
                              </span>}
                          </span>
                        </div>
                        <Button variant="primary" size="sm" data-id="element-3240">
                          <Link to={`/tender/${tender.id}`} className="text-white no-underline" data-id="element-3241">
                            Voir les détails
                          </Link>
                        </Button>
                      </div>
                    </motion.div>)}
                </div> : <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center" data-id="element-3242">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3243">
                    <AlertCircleIcon size={32} className="text-gray-400" data-id="element-3244" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-3245">
                    Aucun résultat trouvé
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-3246">
                    Aucun appel d'offres ne correspond à vos critères de
                    recherche.
                  </p>
                  <Button variant="secondary" onClick={clearAllFilters} data-id="element-3247">
                    Réinitialiser les filtres
                  </Button>
                </div>}
              {/* Pagination */}
              {filteredTenders.length > 0 && <div className="mt-6 flex justify-center" data-id="element-3248">
                  <nav className="flex items-center space-x-2" data-id="element-3249">
                    <button className="w-10 h-10 rounded-md flex items-center justify-center border border-gray-300 text-gray-500 hover:border-blue-600 hover:text-blue-600" data-id="element-3250">
                      <ChevronDownIcon size={18} className="rotate-90" data-id="element-3251" />
                    </button>
                    <button className="w-10 h-10 rounded-md flex items-center justify-center bg-blue-600 text-white font-medium" data-id="element-3252">
                      1
                    </button>
                    <button className="w-10 h-10 rounded-md flex items-center justify-center border border-gray-300 text-gray-700 hover:border-blue-600 hover:text-blue-600" data-id="element-3253">
                      2
                    </button>
                    <button className="w-10 h-10 rounded-md flex items-center justify-center border border-gray-300 text-gray-700 hover:border-blue-600 hover:text-blue-600" data-id="element-3254">
                      3
                    </button>
                    <span className="text-gray-500" data-id="element-3255">...</span>
                    <button className="w-10 h-10 rounded-md flex items-center justify-center border border-gray-300 text-gray-700 hover:border-blue-600 hover:text-blue-600" data-id="element-3256">
                      12
                    </button>
                    <button className="w-10 h-10 rounded-md flex items-center justify-center border border-gray-300 text-gray-500 hover:border-blue-600 hover:text-blue-600" data-id="element-3257">
                      <ChevronDownIcon size={18} className="rotate-270" data-id="element-3258" />
                    </button>
                  </nav>
                </div>}
              {/* Premium features banner */}
              <div className="mt-8 bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl shadow-lg p-6 text-white relative overflow-hidden" data-id="element-3259">
                <div className="absolute top-0 right-0 w-40 h-40 bg-blue-500 rounded-full opacity-20 transform translate-x-1/3 -translate-y-1/3" data-id="element-3260"></div>
                <div className="absolute bottom-0 left-0 w-20 h-20 bg-yellow-500 rounded-full opacity-20 transform -translate-x-1/2 translate-y-1/2" data-id="element-3261"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-3262">
                  <div className="mb-4 md:mb-0 md:mr-6" data-id="element-3263">
                    <h3 className="text-xl font-bold mb-2" data-id="element-3264">
                      Fonctionnalités Premium
                    </h3>
                    <p className="text-blue-100" data-id="element-3265">
                      Profitez de toutes les fonctionnalités exclusives de votre
                      compte Premium Afrique.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3" data-id="element-3266">
                    <Button variant="secondary" className="bg-white text-blue-700 hover:bg-blue-50 border-none" data-id="element-3267">
                      <DownloadIcon size={16} className="mr-2" data-id="element-3268" />
                      Exporter les résultats
                    </Button>
                    <Button variant="primary" className="bg-yellow-500 hover:bg-yellow-600 border-none" data-id="element-3269">
                      <BellIcon size={16} className="mr-2" data-id="element-3270" />
                      Configurer une alerte
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>;
}